import { useState, useEffect } from 'react';

interface OfflineStatus {
  isOnline: boolean;
  isAuthenticated: boolean;
  canWorkOffline: boolean;
  onRetry?: () => void;
}

declare global {
  interface Window {
    __offlineStatus?: OfflineStatus;
  }
}

/**
 * Hook to access the global offline status from the root single-spa application
 * This allows micro-apps to display appropriate offline indicators in their headers
 */
export const useOfflineStatus = (): OfflineStatus => {
  const [offlineStatus, setOfflineStatus] = useState<OfflineStatus>(() => {
    // Initialize from global state if available
    return (
      window.__offlineStatus || {
        isOnline: navigator.onLine,
        isAuthenticated: false,
        canWorkOffline: false,
      }
    );
  });

  useEffect(() => {
    // Listen for offline status changes from the root app
    const handleOfflineStatusChange = (event: CustomEvent<OfflineStatus>) => {
      setOfflineStatus(event.detail);
    };

    // Also check for initial state
    if (window.__offlineStatus) {
      setOfflineStatus(window.__offlineStatus);
    }

    window.addEventListener(
      'offline-status-changed',
      handleOfflineStatusChange as EventListener
    );

    // Fallback: Listen to browser online/offline events
    const handleOnline = () =>
      setOfflineStatus((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setOfflineStatus((prev) => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener(
        'offline-status-changed',
        handleOfflineStatusChange as EventListener
      );
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return offlineStatus;
};
